import { useState } from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import { Alert, AlertDescription } from '@factorialco/f0-react/dist/experimental'
import {
  NotifySyncPolicyPeriodSilaeFortifyDocument,
  NotifySyncPolicyPeriodSilaeFortifyMutation,
  SilaeFortifySyncSupplementsFfEnabledDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useGetMarketplaceCompanyInstallations } from 'modules/marketplace/hooks/integrations/companyInstallations/useGetMarketplaceCompanyInstallations'
import integrationCodes from 'modules/marketplace/lib/integrationCodes'

interface NotifySyncButtonProps {
  periodId: string | number | undefined
}

const NotifySyncButton = ({ periodId }: NotifySyncButtonProps) => {
  const { company } = useCurrentSession()
  const [syncModalVisible, setSyncModalVisible] = useState(false)
  const onCloseSyncModal = () => {
    setSyncModalVisible(false)
  }

  const [notifySync, { loading }] = useMutation(NotifySyncPolicyPeriodSilaeFortifyDocument, {
    extractorFn: (data: NotifySyncPolicyPeriodSilaeFortifyMutation) =>
      data.integrationsSilaeFortifyMutations.syncIntegrationssilaefortifyPolicyPeriod,
  })

  const { data: ffEnabledData } = useQuery(SilaeFortifySyncSupplementsFfEnabledDocument, {
    variables: { companyId: company.id },
  })

  const ffEnabled = ffEnabledData?.features.featuresConnection.nodes[0]?.enabled ?? false

  const onSyncSuplementsWithSilae = async () => {
    const companyId = company.id
    const result = await notifySync({
      variables: { companyId, id: Number(periodId || 0) },
    })
    const handled = handleResult(result, (_errors) => {})
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('payroll_integrations_silae_fortify.sync_supplements.notification_success'),
      })
    }
    onCloseSyncModal()
  }

  const { data: companyInstallationsData } = useGetMarketplaceCompanyInstallations(
    {
      companyIds: [company.id],
      integrationDatoIds: [integrationCodes.silae_fortify],
    },
    false
  )

  const isSilaeFortifyInstalled = companyInstallationsData.find(
    (data) => data.integrationDatoId === integrationCodes.silae_fortify
  )
  const isButtonVisible = ffEnabled && isSilaeFortifyInstalled && periodId

  const handleSyncButton = () => {
    setSyncModalVisible(true)
    track(trackingEvents.payroll.userClicksSyncData, {
      company_id: company.id,
    })
  }

  return (
    <>
      {isButtonVisible && (
        <Button type="secondary" onClick={handleSyncButton} iconName="link">
          {i18n.t('payroll_integrations_silae_fortify.sync_supplements.button')}
        </Button>
      )}
      {syncModalVisible && (
        <Modal
          illustration="URL"
          title={i18n.t('payroll_integrations_silae_fortify.sync_supplements.title')}
          description={i18n.t('payroll_integrations_silae_fortify.sync_supplements.description')}
          onClose={onCloseSyncModal}
          footer={
            <>
              <Button type="secondary" onClick={onCloseSyncModal}>
                {i18n.t('common.cancel')}
              </Button>
              <Button onClick={onSyncSuplementsWithSilae} loading={loading}>
                {i18n.t('payroll_integrations.sync.sync_confirm')}
              </Button>
            </>
          }
        >
          <Alert variant="warning">
            <AlertDescription>
              {i18n.t('payroll_integrations_silae_fortify.sync_supplements.warning')}
            </AlertDescription>
          </Alert>
        </Modal>
      )}
    </>
  )
}

export default NotifySyncButton
